'use client'

// Types
import type { components as authComponents } from '@/types/auth-schema'

// Redux
import { createSlice, PayloadAction } from '@reduxjs/toolkit'

export type AuthUser = authComponents['schemas']['User'] & {
  sub: string // User ID
  iat: number // Issued At
  exp: number // Expiration Time
  company: authComponents['schemas']['Company']
}

export type PermissionEntity =
  | 'SETTINGS'
  | 'VARIATIONS'
  | 'DAYWORKS'
  | 'APPLICATIONS'
  | 'PAYMENT_NOTICES'
  | 'INVOICES'
  | 'SALES_CREDIT_NOTES'
  | 'PURCHASE_ORDERS'
  | 'DELIVERY_NOTES'
  | 'GOODS_RETURN_NOTES'
  | 'INVOICE_CHECKING'
  | 'CREDIT_NOTES'
  | 'DIRECT_COSTS'

export type Permission = {
  entity_name: PermissionEntity
  access_type: 'READ' | 'READ_WRITE' | 'DENY'
  group?: 'CONTRACT_MANAGEMENT' | 'PROCUREMENT' | 'SETTINGS'
}

type AuthState = {
  authenticated: boolean
  user: AuthUser | null
  company: authComponents['schemas']['Company'] | null
  connections?:
    | {
        xero?: {
          id: string
          name: string
        }
        sage?: {
          id: string
          name: string
        }
      }
    | {}
  permissions?: Permission[]
}

const initialState: AuthState = {
  authenticated: false,
  user: null,
  company: null,
  permissions: [],
}

const auth = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setAuthData(state, action: PayloadAction<AuthUser>) {
      const data = action.payload

      state.authenticated = true
      state.user = data
      state.company = data.company || null // Assign company from user data
      state.connections = data.connections || {} // Assign connections from user data
      state.permissions = data.permissions || [] // Assign permissions from user data
    },
    updateUser(state, action: PayloadAction<Partial<authComponents['schemas']['User']>>) {
      if (state.user) {
        state.user = { ...state.user, ...action.payload }

        if (action.payload.company && state.company) {
          state.company = { ...state.company, ...action.payload.company }
        } else if (action.payload.company) {
          state.company = action.payload.company
        }

        if (action.payload.connections && state.connections) {
          state.connections = { ...state.connections, ...action.payload.connections }
        } else if (action.payload.connections) {
          state.connections = action.payload.connections
        }
      }
    },
    clearAuthData() {
      return initialState
    },
  },
})

export type TAuthState = AuthState

export default auth.reducer

export const { setAuthData, updateUser, clearAuthData } = auth.actions
